import type { JSX, Ref } from 'react';
import { forwardRef, Fragment, useCallback, useMemo, useState } from 'react';
import type { BaseSelectContextProps, SelectFilterItem, SelectItems } from '~/components/granular';
import { Divider, isSelectFilterItemGroupArray, useBaseSelectContext } from '~/components/granular';
import { BackButton } from '../components/back-button';
import { Loading } from '../components/loading';
import { NoData } from '../components/no-data';
import { SearchInput } from '../components/search-input';
import { SelectOption } from '../components/select-option';
import { SelectGroupHeading } from '../components/SelectGroupHeading';
import { DropdownSelect } from '../dropdown-select/DropdownSelect';
import type { HierarchicalSelectProps } from './HierarchicalSelect.props';

/* -------------------------------------------------------------------------------------------------
 * HierarchicalSelect
 * -----------------------------------------------------------------------------------------------*/

function HierarchicalSelectComponent<TItemValue>(
  {
    appearance = 'filter',
    applyLabel,
    backLabel,
    disableFiltering,
    disabled,
    indeterminateItems,
    items = [],
    label,
    leftIcon,
    loading,
    noDataLabel,
    placeholder,
    searchPlaceholder,
    selectedItems = [],
    selectedLimit,
    size = 'x-small',
    width,
    trigger,
    onGroupSelection,
    onSelectChange,
  }: HierarchicalSelectProps<TItemValue>,
  ref: Ref<HTMLDivElement>,
) {
  return (
    <DropdownSelect
      appearance={appearance}
      applyLabel={applyLabel}
      backLabel={backLabel}
      disabled={disabled}
      disableFiltering={disableFiltering}
      indeterminateItems={indeterminateItems}
      items={items}
      label={label}
      leftIcon={leftIcon}
      loading={loading}
      noDataLabel={noDataLabel}
      onSelectChange={onSelectChange}
      placeholder={placeholder}
      ref={ref}
      searchPlaceholder={searchPlaceholder}
      selectedItems={selectedItems}
      selectedLimit={selectedLimit}
      size={size}
      trigger={trigger}
      width={width}
    >
      <HierarchicalSelectItems onGroupSelection={onGroupSelection} />
    </DropdownSelect>
  );
}

export const HierarchicalSelect = forwardRef(HierarchicalSelectComponent) as <TItemValue>(
  props: HierarchicalSelectProps<TItemValue> & { ref?: Ref<HTMLDivElement> },
) => JSX.Element;

/* -------------------------------------------------------------------------------------------------
 * HierarchicalSelectItems
 * -----------------------------------------------------------------------------------------------*/

interface HierarchicalSelectItemsProps<TSelectValue> extends Pick<
  HierarchicalSelectProps<TSelectValue>,
  'onGroupSelection'
> {}

function HierarchicalSelectItems<TSelectValue>({ onGroupSelection }: HierarchicalSelectItemsProps<TSelectValue>) {
  const { loading, handleSearchChange, handleSelectChange, toggleOpen, width } = useBaseSelectContext();

  const [activeGroup, setActiveGroup] = useState<SelectFilterItem<TSelectValue> | null>(null);

  function handleGroupClick(item: SelectFilterItem<TSelectValue>) {
    if ('children' in item) {
      handleSearchChange();
      setActiveGroup(item);
      onGroupSelection?.(item);
    }
  }

  function handleBackButtonClick() {
    setActiveGroup(null);
  }

  const childFilter = useMemo(() => {
    return activeGroup?.children({ handleSelectChange, side: activeGroup.side, toggleOpen, width });
  }, [activeGroup, toggleOpen, handleSelectChange, width]);

  if (loading) {
    return <Loading />;
  }

  return !activeGroup ? (
    <HierarchicalSelectList onItemClick={handleGroupClick} />
  ) : (
    <>
      <BackButton onClick={handleBackButtonClick} visible />
      {childFilter}
    </>
  );
}

/* -------------------------------------------------------------------------------------------------
 * HierarchicalSelectList
 * -----------------------------------------------------------------------------------------------*/

function HierarchicalSelectList<TSelectValue>({
  onItemClick,
}: {
  onItemClick: (item: SelectFilterItem<TSelectValue>) => void;
}) {
  const { items } = useBaseSelectContext<BaseSelectContextProps<TSelectValue>>();

  return (
    <>
      <SearchInput autoFocus />

      {!items.length && <NoData />}

      <div className="max-h-[300px] min-h-0 overflow-y-auto">
        <HierarchicalSelectOptions items={items} onItemClick={onItemClick} />
      </div>
    </>
  );
}

/* -------------------------------------------------------------------------------------------------
 * HierarchicalSelectOptions
 * -----------------------------------------------------------------------------------------------*/

function HierarchicalSelectOptions<TSelectValue>({
  items,
  onItemClick,
}: {
  items: Array<SelectItems<TSelectValue>>;
  onItemClick: (item: SelectFilterItem<TSelectValue>) => void;
}) {
  const displayDivider = useCallback((index: number) => index !== items.length - 1, [items]);

  if (isSelectFilterItemGroupArray(items)) {
    return items.map((item, index) => {
      if (item.filters.length === 0) {
        return null;
      }

      return (
        <Fragment key={String(item.value)}>
          <SelectGroupHeading>{item.label}</SelectGroupHeading>

          {item.filters?.map((child) => (
            <SelectOption className="h-8" item={child} key={String(child.value)} onItemClick={onItemClick}>
              {child.label}
            </SelectOption>
          ))}
          {displayDivider(index) && <Divider className="w-full" />}
        </Fragment>
      );
    });
  }

  return (items as Array<SelectFilterItem<TSelectValue>>).map((item) => (
    <SelectOption className="h-8" item={item} key={String(item.value)} onItemClick={onItemClick}>
      {item.label}
    </SelectOption>
  ));
}
